import React, { Component } from 'react';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { Mail, Users, UserCog, Send, Plus, Edit3, Trash2, Eye, CheckCircle, XCircle, Clock, X, BarChart3, ArrowRight, Zap } from 'lucide-react';
import fetch from '../../../core/fetch';
import history from '../../../core/history';
import s from './NewsletterManager.css';

class NewsletterManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: 'general',
      campaigns: [],
      stats: null,
      loading: true,
      previewCampaign: null,
    };
  }

  componentDidMount() {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'hosts') {
      this.setState({ activeTab: 'hosts' });
    }
    this.loadData();
  }

  loadData = async () => {
    this.setState({ loading: true });
    try {
      await Promise.all([this.loadCampaigns(), this.loadStats()]);
    } finally {
      this.setState({ loading: false });
    }
  };

  loadCampaigns = async () => {
    try {
      const resp = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `query { getNewsletterCampaigns { id title subject htmlContent audienceType status totalRecipients sentCount failedCount sentAt createdAt } }`
        }),
        credentials: 'include'
      });
      const { data } = await resp.json();
      if (data && data.getNewsletterCampaigns) {
        this.setState({ campaigns: data.getNewsletterCampaigns });
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  loadStats = async () => {
    try {
      const resp = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `query { getNewsletterStats { totalSubscribers totalUnsubscribed totalHosts totalCampaignsSent totalEmailsSent } }`
        }),
        credentials: 'include'
      });
      const { data } = await resp.json();
      if (data && data.getNewsletterStats) {
        this.setState({ stats: data.getNewsletterStats });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  handleDelete = async (id) => {
    if (!confirm('Estas seguro de eliminar esta campaña?')) return;
    try {
      const resp = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `mutation { deleteNewsletterCampaign(id: ${id}) { status errorMessage } }`
        }),
        credentials: 'include'
      });
      const { data } = await resp.json();
      if (data && data.deleteNewsletterCampaign && data.deleteNewsletterCampaign.status === 200) {
        this.loadCampaigns();
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
    }
  };

  formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  getStatusClass = (status) => {
    const map = { draft: s.statusDraft, sending: s.statusSending, sent: s.statusSent, failed: s.statusFailed };
    return map[status] || '';
  };

  getStatusLabel = (status) => {
    const map = { draft: 'Borrador', sending: 'Enviando...', sent: 'Enviado', failed: 'Fallido' };
    return map[status] || status;
  };

  getPreviewHTML = (campaign) => {
    if (!campaign || !campaign.htmlContent) return '';
    const processed = campaign.htmlContent
      .replace(/\{userName\}/g, 'Juan Martinez')
      .replace(/\{firstName\}/g, 'Juan')
      .replace(/\{email\}/g, 'juan@ejemplo.com')
      .replace(/\{siteName\}/g, 'NAVEGA');

    const isHostOnly = campaign.audienceType === 'hosts_only';
    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{margin:0;background:#f0f2f5;font-family:system-ui,sans-serif;padding:20px;}</style></head>
<body>
<div style="max-width:600px;margin:0 auto;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">
<div style="background:linear-gradient(135deg,#0a1628,#1a2744,#0d3b66);padding:40px 32px;text-align:center;">
<h1 style="color:#fff;font-size:28px;margin:0;letter-spacing:2px;">NAVEGA</h1>
</div>
<div style="padding:40px 32px;color:#2d3748;font-size:16px;line-height:1.7;">${processed}</div>
<div style="background:#f7fafc;padding:32px;text-align:center;border-top:1px solid #e2e8f0;">
<p style="margin:0 0 8px;font-size:13px;color:#718096;">&copy; ${new Date().getFullYear()} NAVEGA. Todos los derechos reservados.</p>
${!isHostOnly ? '<div style="margin-top:16px;padding-top:16px;border-top:1px solid #e2e8f0;"><a href="#" style="color:#a0aec0;font-size:12px;">Desuscribirse de este boletin</a></div>' : ''}
</div></div></body></html>`;
  };

  render() {
    const { activeTab, campaigns, stats, loading, previewCampaign } = this.state;
    
    const filteredCampaigns = campaigns.filter(c => 
      activeTab === 'general' ? c.audienceType === 'all_users' : c.audienceType === 'hosts_only'
    );

    const generalCount = campaigns.filter(c => c.audienceType === 'all_users').length;
    const hostsCount = campaigns.filter(c => c.audienceType === 'hosts_only').length;

    const activeRecipients = activeTab === 'general'
      ? (stats ? stats.totalSubscribers - stats.totalUnsubscribed : 0)
      : (stats ? stats.totalHosts : 0);

    return (
      <div className={s.nlManager}>
        {/* Hero Header con Stats */}
        <div className={s.heroHeader}>
          <div className={s.heroTitle}>Centro de Boletin</div>
          <div className={s.heroSubtitle}>Gestiona tus campañas de email y comunicados</div>
          
          {stats && (
            <div className={s.statsGrid}>
              <div className={s.statCard}>
                <div className={`${s.statIconWrap} ${s.statIconBlue}`}><Users size={18} /></div>
                <div className={s.statValue}>{stats.totalSubscribers}</div>
                <div className={s.statLabel}>Suscriptores</div>
              </div>
              <div className={s.statCard}>
                <div className={`${s.statIconWrap} ${s.statIconRed}`}><XCircle size={18} /></div>
                <div className={s.statValue}>{stats.totalUnsubscribed}</div>
                <div className={s.statLabel}>Desuscritos</div>
              </div>
              <div className={s.statCard}>
                <div className={`${s.statIconWrap} ${s.statIconGreen}`}><UserCog size={18} /></div>
                <div className={s.statValue}>{stats.totalHosts}</div>
                <div className={s.statLabel}>Propietarios</div>
              </div>
              <div className={s.statCard}>
                <div className={`${s.statIconWrap} ${s.statIconPurple}`}><Send size={18} /></div>
                <div className={s.statValue}>{stats.totalCampaignsSent}</div>
                <div className={s.statLabel}>Campañas</div>
              </div>
              <div className={s.statCard}>
                <div className={`${s.statIconWrap} ${s.statIconAmber}`}><Mail size={18} /></div>
                <div className={s.statValue}>{stats.totalEmailsSent}</div>
                <div className={s.statLabel}>Emails Enviados</div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className={s.tabsContainer}>
          <button
            className={`${s.tabBtn} ${activeTab === 'general' ? s.tabBtnActive : ''}`}
            onClick={() => this.setState({ activeTab: 'general' })}
          >
            <Mail size={16} /> Boletin General
            <span className={s.tabBadge}>{generalCount}</span>
          </button>
          <button
            className={`${s.tabBtn} ${activeTab === 'hosts' ? s.tabBtnActive : ''}`}
            onClick={() => this.setState({ activeTab: 'hosts' })}
          >
            <UserCog size={16} /> Comunicados Propietarios
            <span className={s.tabBadge}>{hostsCount}</span>
          </button>
        </div>

        {/* Audience Bar - Muestra cantidad de destinatarios */}
        <div className={s.audienceBar}>
          <div className={s.audienceInfo}>
            <div className={s.audienceIcon}>
              {activeTab === 'general' ? <Users size={22} /> : <UserCog size={22} />}
            </div>
            <div className={s.audienceText}>
              <div className={s.audienceCount}>
                {activeRecipients} destinatarios
              </div>
              <div className={s.audienceLabel}>
                {activeTab === 'general'
                  ? `${stats ? stats.totalSubscribers : 0} registrados - ${stats ? stats.totalUnsubscribed : 0} desuscritos`
                  : 'Propietarios con embarcaciones publicadas'}
              </div>
            </div>
          </div>
          <button 
            className={s.createBtn}
            onClick={() => history.push(`/siteadmin/newsletter/create?type=${activeTab === 'general' ? 'all_users' : 'hosts_only'}`)}
          >
            <Plus size={16} /> Nueva Campaña
          </button>
        </div>

        {/* Campaign Table / Empty State */}
        {loading ? (
          <div className={s.campaignTable}>
            {[1,2,3].map(i => (
              <div key={i} className={s.skeletonCard}>
                <div className={s.skeleton} style={{ width: '60%', height: '18px' }} />
                <div className={s.skeleton} style={{ width: '40%', height: '14px' }} />
              </div>
            ))}
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}>
              <Mail size={32} />
            </div>
            <div className={s.emptyTitle}>
              {activeTab === 'general' ? 'Sin campañas de boletin' : 'Sin comunicados a propietarios'}
            </div>
            <div className={s.emptyText}>
              Crea tu primera campaña para enviar emails a {activeTab === 'general' ? `${activeRecipients} suscriptores` : `${activeRecipients} propietarios`} registrados en la plataforma.
            </div>
            <button 
              className={s.emptyBtn}
              onClick={() => history.push(`/siteadmin/newsletter/create?type=${activeTab === 'general' ? 'all_users' : 'hosts_only'}`)}
            >
              <Zap size={16} /> Crear Primera Campaña
            </button>
          </div>
        ) : (
          <div className={s.campaignTable}>
            <div className={s.tableHeader}>
              <div className={s.th}>Campaña</div>
              <div className={s.th}>Fecha</div>
              <div className={s.th}>Estado</div>
              <div className={s.th}>Resultados</div>
              <div className={s.th} style={{ textAlign: 'right' }}>Acciones</div>
            </div>
            {filteredCampaigns.map(campaign => {
              const successRate = campaign.totalRecipients > 0
                ? Math.round((campaign.sentCount / campaign.totalRecipients) * 100)
                : 0;
              
              return (
                <div key={campaign.id} className={s.campaignRow}>
                  <div>
                    <div className={s.campTitle}>{campaign.title}</div>
                    <div className={s.campSubject}>{campaign.subject}</div>
                  </div>
                  <div className={s.campDate}>
                    {this.formatDate(campaign.sentAt || campaign.createdAt)}
                  </div>
                  <div>
                    <span className={`${s.statusBadge} ${this.getStatusClass(campaign.status)}`}>
                      {campaign.status === 'sending' && <Clock size={12} />}
                      {campaign.status === 'sent' && <CheckCircle size={12} />}
                      {campaign.status === 'failed' && <XCircle size={12} />}
                      {this.getStatusLabel(campaign.status)}
                    </span>
                  </div>
                  <div className={s.campStats}>
                    {campaign.status === 'sent' || campaign.status === 'sending' ? (
                      <>
                        <div className={s.campStatRow}>
                          <CheckCircle size={13} color="#10b981" /> {campaign.sentCount} / {campaign.totalRecipients}
                        </div>
                        {campaign.failedCount > 0 && (
                          <div className={s.campStatRow}>
                            <XCircle size={13} color="#ef4444" /> {campaign.failedCount} fallidos
                          </div>
                        )}
                        <div className={s.progressMini}>
                          <div className={s.progressMiniFill} style={{ width: `${successRate}%` }} />
                        </div>
                      </>
                    ) : (
                      <div className={s.campStatRow} style={{ color: '#94a3b8' }}>—</div>
                    )}
                  </div>
                  <div className={s.campActions}>
                    <button
                      className={`${s.actionBtn} ${s.actionBtnPreview}`}
                      onClick={() => this.setState({ previewCampaign: campaign })}
                      title="Vista previa"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      className={s.actionBtn}
                      onClick={() => history.push(`/siteadmin/newsletter/edit/${campaign.id}`)}
                    >
                      {campaign.status === 'draft' ? <><Edit3 size={14} /> Editar</> : <><BarChart3 size={14} /> Ver</>}
                    </button>
                    {campaign.status === 'draft' && (
                      <button
                        className={`${s.actionBtn} ${s.actionBtnDanger}`}
                        onClick={() => this.handleDelete(campaign.id)}
                        title="Eliminar"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Preview Side Panel */}
        {previewCampaign && (
          <>
            <div className={s.previewOverlay} onClick={() => this.setState({ previewCampaign: null })} />
            <div className={s.previewPanel}>
              <div className={s.previewPanelHeader}>
                <div className={s.previewPanelTitle}>Vista Previa del Email</div>
                <button className={s.previewPanelClose} onClick={() => this.setState({ previewCampaign: null })}>
                  <X size={20} />
                </button>
              </div>
              <div className={s.previewPanelMeta}>
                <div className={s.previewMetaRow}>
                  <span className={s.previewMetaLabel}>Asunto:</span>
                  <span className={s.previewMetaValue}>{previewCampaign.subject}</span>
                </div>
                <div className={s.previewMetaRow}>
                  <span className={s.previewMetaLabel}>Audiencia:</span>
                  <span className={s.previewMetaValue}>
                    {previewCampaign.audienceType === 'hosts_only' ? 'Solo Propietarios' : 'Todos los Suscriptores'}
                    {' '}({activeRecipients} destinatarios)
                  </span>
                </div>
                <div className={s.previewMetaRow}>
                  <span className={s.previewMetaLabel}>Estado:</span>
                  <span className={`${s.statusBadge} ${this.getStatusClass(previewCampaign.status)}`}>
                    {this.getStatusLabel(previewCampaign.status)}
                  </span>
                </div>
              </div>
              <div className={s.previewPanelBody}>
                <iframe
                  className={s.previewFrame}
                  srcDoc={this.getPreviewHTML(previewCampaign)}
                  title="Email Preview"
                />
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
}

export default withStyles(s)(NewsletterManager);
